// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  FieldSelect,
  FieldText,
  SectionCard,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import BusinessValidityChangeNotice from "components/BusinessValidityChangeNotice";
import { Icon } from "components/ShellV2";

// Utils
import { isValidNif, isValidPhone } from "utils/validations";
import { CommunityOrAggregatorTypes } from "utils/constants/index";
import { translateOptions } from "utils/constants/enumLabels";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

function BasicInfo({ data, onUpdate, isLoading, profileOnly }) {
  const initialStartDate = data?.start_date || lisbonToday();
  const initialEndDate = data?.end_date || defaultBusinessEndDate(initialStartDate);
  const [original, setOriginal] = useState({
    ...data,
    start_date: initialStartDate,
    end_date: initialEndDate,
  });

  const [name, setName] = useState(data && data.name);
  const [identifier, setIdentifier] = useState(data && data.identifier);
  const [phone, setPhone] = useState(data && data.phone);
  const [address, setAddress] = useState(data && data.address);
  const [nif, setNif] = useState(data && data.nif);
  const [communityOrAggregatorType, setCommunityOrAggregatorType] = useState(
    data && data.community_or_aggregator_type ? data.community_or_aggregator_type : "EGAC"
  );
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const { t } = useTranslation(["community", "common"]);

  const canSave =
    !isLoading &&
    !(
      original.name === name &&
      original.identifier === identifier &&
      original.phone === phone &&
      original.address === address &&
      original.nif === nif &&
      (profileOnly ||
        ((original.community_or_aggregator_type ?? "EGAC") === communityOrAggregatorType &&
          original.start_date === startDate &&
          original.end_date === endDate))
    ) &&
    Boolean(identifier?.trim()) &&
    isValidPhone(phone) &&
    isValidNif(nif) &&
    startDate &&
    (!endDate || endDate >= startDate);

  const onUpdateClicked = async () => {
    if (canSave) {
      const newData = profileOnly
        ? { name, identifier: identifier.trim(), phone, address, nif }
        : {
            name,
            identifier: identifier.trim(),
            phone,
            address,
            nif,
            community_or_aggregator_type: communityOrAggregatorType,
            start_date: startDate,
            end_date: endDate || null,
          };

      const success = await onUpdate(newData, t("basic_info.updated_ok"));
      if (success) {
        setOriginal({ ...newData, end_date: endDate });
      }
    }
  };

  return (
    <SectionCard
      id="general"
      icon="badge"
      title={t("basic_info.title")}
      action={
        <Button variant="ghost" size="sm" disabled={!canSave} onClick={onUpdateClicked}>
          <Icon name="save" />
          {t("basic_info.update")}
        </Button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldText label={t("basic_info.name")} value={name} onChange={setName} />
        <FieldText
          label={t("basic_info.identifier")}
          value={identifier}
          onChange={setIdentifier}
          required
          error={!identifier?.trim()}
        />
        <FieldText
          label={t("basic_info.phone")}
          value={phone}
          onChange={setPhone}
          error={!isValidPhone(phone)}
        />
        <FieldText label={t("basic_info.address")} value={address} onChange={setAddress} />
        <FieldText
          label={t("basic_info.nif")}
          value={nif}
          onChange={setNif}
          error={!isValidNif(nif)}
        />
        {!profileOnly && (
          <FieldSelect
            label={t("basic_info.community_or_aggregator_type", {
              defaultValue: "Tipo de Comunidade/Agregador",
            })}
            value={communityOrAggregatorType}
            options={translateOptions(CommunityOrAggregatorTypes, "communityOrAggregator")}
            onChange={setCommunityOrAggregatorType}
          />
        )}
        {!profileOnly && (
          <ValidityDateRangePicker
            value={{ start: startDate, end: endDate }}
            onChange={({ start, end }) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        )}
      </div>
      {!profileOnly && (
        <BusinessValidityChangeNotice
          originalStartDate={original.start_date}
          originalEndDate={original.end_date}
          startDate={startDate}
          endDate={endDate}
          className="mt-4"
        />
      )}
    </SectionCard>
  );
}

BasicInfo.defaultProps = {
  isLoading: false,
  profileOnly: false,
};

BasicInfo.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    nif: PropTypes.string,
    methodAllocCoeff: PropTypes.string,
    identifier: PropTypes.string,
    community_or_aggregator_type: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  profileOnly: PropTypes.bool,
};

export default BasicInfo;
